import React, { useState } from "react";
import FileInput, { FileDetails } from "@amzn/meridian/file-input";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import { Space } from "../space/Space";
import Icon from "@amzn/meridian/icon";
import checkKnockoutTokens from "@amzn/meridian-tokens/base/icon/check-knockout";
import errorKnockoutTokens from "@amzn/meridian-tokens/base/icon/error-knockout";
import Divider from "@amzn/meridian/divider";
import Row from "@amzn/meridian/row";
import { useLazyAuditFileUploadQuery } from "../../endpoints/auditEventTrigger";
import { useAppDispatch, useAppSelector } from "../../app/store/hooks";
import {
    appendDateStampToFileName,
    calculateSpeedInKB,
    getDocumentType,
    MAX_FILE_AMOUNT,
    MAX_FILE_SIZE,
    toMB,
    uploadFileAndGenerateAudit,
} from "./utils";
import Loader from "@amzn/meridian/loader";
import { useTable } from "../table/hooks";
import { useIngestDataSourceFileMutation } from "../../endpoints/ingestDataSource";
import Button from "@amzn/meridian/button";

interface UploadProps {
    dataSourceType?: string | null;
    dataType?: string | null;
    country?: string | null;
    useCase: string;
    s3Prefix: string;
    acceptedFileTypes: string;
}

export const Upload: React.FC<UploadProps> = ({
    dataSourceType = null,
    dataType = null,
    country = null,
    useCase,
    s3Prefix,
    acceptedFileTypes,
}) => {
    const [auditGenerated, setAuditGenerated] = useState<Map<string, any>>(new Map());
    const [triggerAudit] = useLazyAuditFileUploadQuery();
    const [triggerIngest] = useIngestDataSourceFileMutation();
    const { alias } = useAppSelector(state => state.auth.user);
    const [acceptedFiles, setAcceptedFiles] = useState([]);
    const [rejectedFiles, setRejectedFiles] = useState([]);
    const [tooManyFilesError, setTooManyFilesError] = useState(false);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { updateCurrentTableId } = useTable();

    const onFileAttached = (accepted, rejected) => {
        if (
            rejected.length + accepted.length + rejectedFiles.length + acceptedFiles.length >
            MAX_FILE_AMOUNT
        ) {
            setTooManyFilesError(true);

            return;
        }

        accepted.forEach(file => {
            file.uniqueName = appendDateStampToFileName(file?.name);
            file.error = Number(toMB(file.size)) > MAX_FILE_SIZE;
            file.errorMessage =
                Number(toMB(file.size)) > MAX_FILE_SIZE
                    ? "File too large. Please upload a file under 300MB."
                    : "";
        });

        const rejectedFilesList = rejected.map(f => {
            const file = f.file;
            file.uniqueName = appendDateStampToFileName(file?.name);
            file.error = true;
            file.errorMessage = "File has an invalid format. Only pdf files are accepted.";

            return file;
        });

        setAcceptedFiles([...acceptedFiles, ...accepted.filter(file => file?.error === false)]);
        setRejectedFiles([
            ...rejectedFiles,
            ...rejectedFilesList,
            ...accepted.filter(file => file?.error === true),
        ]);
        setTooManyFilesError(false);
    };

    const onClickRemoveFiles = () => {
        setAcceptedFiles([]);
        setRejectedFiles([]);
    };

    const onClickCancel = file => {
        if (file.error) {
            setRejectedFiles(rejectedFiles.filter(f => f?.uniqueName !== file?.uniqueName));
        } else {
            setAcceptedFiles(acceptedFiles.filter(f => f?.uniqueName !== file?.uniqueName));
        }
    };
    const onClickSubmit = () => {
        Promise.allSettled(
            acceptedFiles.map(async file => {
                return await uploadFileAndGenerateAudit(
                    file,
                    setIsDisabled,
                    setAuditGenerated,
                    auditGenerated,
                    triggerAudit,
                    triggerIngest,
                    alias,
                    useCase,
                    s3Prefix,
                    dataSourceType,
                    country,
                    dataType
                );
            })
        ).then(() => {
            dispatch(updateCurrentTableId());
            setIsDisabled(false);
        });
    };

    // Function to get a readable file type display string
    const getReadableFileTypes = (fileTypesString: string) => {
        if (!fileTypesString) return "";

        // Split the string into an array of MIME types
        const mimeTypes = fileTypesString.split(",");

        // Convert each MIME type to its simplified form and get unique values
        const simplifiedTypes = [
            ...new Set(mimeTypes.map(mimeType => getDocumentType(mimeType))),
        ].filter(Boolean); // Remove empty strings (from unrecognized types)

        // Join the unique simplified types and convert to uppercase
        return simplifiedTypes.join(", ").toUpperCase();
    };

    return (
        <Column width={"40rem"}>
            <Row>
                <Column spacing={"none"}>
                    <Text type={"h200"}>Upload file</Text>
                    <Text type={"b200"}>
                        {getReadableFileTypes(acceptedFileTypes)} files only. Max size: 300MB.
                    </Text>
                    <Space size={"s"} />
                    <FileInput
                        aria-border-color={"red"}
                        showDropZone={true}
                        accept={acceptedFileTypes}
                        disabled={isDisabled || isSubmitted}
                        uploadButtonSize={"large"}
                        type="multiple"
                        onFileAttached={(acceptedFiles, rejectedFiles) =>
                            onFileAttached(acceptedFiles, rejectedFiles)
                        }
                        uploadButtonType={"tertiary"}
                    ></FileInput>
                    <Space size={"s"} />
                    {tooManyFilesError ? (
                        <Text type={"b200"}>
                            <Icon tokens={errorKnockoutTokens} color="error"></Icon> Too many files.
                            Maximum 50 files allowed per upload.
                        </Text>
                    ) : null}
                    <Space size={"s"} />
                </Column>
                <Button
                    onClick={() => {
                        setIsSubmitted(false);
                        setTooManyFilesError(false);
                        onClickRemoveFiles();
                    }}
                    type={"tertiary"}
                    disabled={isDisabled}
                >
                    {isSubmitted && !isDisabled ? "Clear" : "Cancel"}
                </Button>
                <Button
                    onClick={() => {
                        onClickSubmit();
                        setIsSubmitted(true);
                    }}
                    disabled={
                        isDisabled ||
                        rejectedFiles.length > 0 ||
                        isSubmitted ||
                        acceptedFiles.length === 0 // Disable if no valid files
                    }
                >
                    Submit
                </Button>
            </Row>
            <Column
                maxHeight={"25rem"}
                maxWidth={"30rem"}
                overflowY={"scroll"}
                spacingInset={"0 300 0 0"}
            >
                {[...acceptedFiles, ...rejectedFiles].map(file => {
                    return (
                        <Column>
                            <FileDetails
                                key={file?.uniqueName}
                                file={file}
                                onClickCancel={file.error ? () => onClickCancel(file) : null}
                                onClickRemoveFile={!isSubmitted ? () => onClickCancel(file) : null}
                                error={file.error}
                                errorMessage={file.errorMessage}
                                connectionSpeed={`${calculateSpeedInKB(auditGenerated.get(file?.uniqueName))}`}
                                cancelUploadButtonLabel={`Cancel, ${file?.name ?? ""}`}
                                removeFileButtonLabel={`Delete, ${file?.name ?? ""}`}
                                uploadCompleteLabel={
                                    !isSubmitted
                                        ? "Ready to upload"
                                        : auditGenerated.get(file?.uniqueName)?.progress === 100
                                          ? "Upload complete"
                                          : null
                                }
                                uploadPercentage={
                                    auditGenerated.get(file?.uniqueName)?.progress ?? 0
                                }
                                uploadComplete={
                                    file.error ||
                                    !isSubmitted ||
                                    auditGenerated.get(file?.uniqueName)?.progress === 100
                                }
                            />
                            {auditGenerated.get(file.uniqueName)?.auditGenerated ? (
                                <Text type={"b200"}>
                                    <Icon tokens={checkKnockoutTokens} color="success"></Icon>
                                    &nbsp; Audit generated
                                </Text>
                            ) : auditGenerated.get(file.uniqueName)?.isError ? (
                                <Text type={"b200"}>
                                    <Icon tokens={errorKnockoutTokens} color="error"></Icon>{" "}
                                    Unexpected error occurred while attempting to generate audit.
                                </Text>
                            ) : auditGenerated.get(file.uniqueName)?.isLoading ? (
                                <Row spacing={"none"}>
                                    <Loader size="small" /> &nbsp;{" "}
                                    <Text type={"b200"}>Generating audit</Text>
                                </Row>
                            ) : null}

                            <Divider />
                        </Column>
                    );
                })}
            </Column>
        </Column>
    );
};
